/**
 * browserSignInPhase.ts — where a sign-in in Prest0n's browser stands, for every card that asks for it
 * (slice D of the browser sign-in program, Miles's ruling R1, 2026-10-01: "a sign-in button stays usable
 * until the sign-in is confirmed").
 *
 * ONE state model. A sign-in card in the chat used to answer a press by greying itself out for good
 * ("opened ✓", disabled), so a window that never appeared, or one closed half way, left a dead button and no
 * way back in. Now the card says where the sign-in stands, and a press always does something:
 *
 *   not-started   the card's own words ("open eFax sign-in")
 *   opening       "opening…" — the window is on its way; a press brings it forward
 *   open          "continue in the window"
 *   not-finished  "continue signing in" — the window closed, or could not start, before the sign-in finished
 *   signed-in     "signed in ✓" — the person finished it; the button does nothing more
 *   signed-out    "sign in again" — the record says the sign-in was lost
 *
 * The window reports (DesktopSignInModal: opening on mount, open when live, not-finished on a failed boot,
 * signed-in beside its announcement), its host reports a close, and the press reports opening. Every card for
 * the same app shows the same phase: the cards are told through `subscribeBrowserSignInPhase` and the DOM
 * event below (browserSignInCards.ts paints them). The words are browserSignInPhaseCopy.ts's.
 *
 * Keyed by the connect lane's one app key (connectAppOfferKey), so "Ring Central" and "RingCentral" are one
 * sign-in. In memory only: a reload reads the connection's record instead (connectTileRehydrate.ts).
 *
 * The store also keeps the connection's slug, but only one the person's OWN press registered
 * (`rememberBrowserSignInSlug`, from the registration's answer): the next press, on any card for the app,
 * reopens on it instead of registering again. A slug a card hands in (a "sign in again" card's) serves that
 * press only. A workspace can hold a teammate's record under the same slug, and finishing a sign-in onto it
 * is refused, so a slug is never passed on from a card or a report.
 *
 * A leaf: it imports only connectAppKey.ts, which imports nothing.
 */
import { connectAppOfferKey } from './connectAppKey';

export type BrowserSignInPhase = 'not-started' | 'opening' | 'open' | 'not-finished' | 'signed-in' | 'signed-out';

/** Every phase, in the order a sign-in moves through them. */
export const BROWSER_SIGNIN_PHASES: readonly BrowserSignInPhase[] = [
  'not-started', 'opening', 'open', 'not-finished', 'signed-in', 'signed-out',
];

/** The phases a window being open (or on its way) puts every card in, whenever the card was posted. */
export const BROWSER_SIGNIN_WINDOW_PHASES: readonly BrowserSignInPhase[] = ['opening', 'open', 'not-finished'];

/** Fired on `document` whenever a sign-in's phase changes: { appKey, appName, phase, slug?, atMs, seq }. */
export const BROWSER_SIGNIN_PHASE_EVENT = 'prest0n:browser-signin-phase';

export interface BrowserSignInPhaseState {
  appKey: string;
  /** The name the last report used (a card's own `data-app-name`, or the window's). */
  appName: string;
  phase: BrowserSignInPhase;
  /** The slug the person's own press registered, once known: the next press reopens on it. */
  slug?: string;
  /** When this phase was reported (ms). */
  atMs: number;
  /** One number per report, increasing: two reports in the same millisecond are still ordered. */
  seq: number;
  /** 2026-10-31: who the sign-in was made as, once the window finished it ("signed in as …"). Only with `signed-in`. */
  signedInAs?: string;
}

/** The server's custom-connection slug shape (connectApp.ts CUSTOM_CONNECTION_SLUG_RE, the same literal). */
const SLUG_RE = /^custom-[a-z0-9_]{1,50}$/;

const states = new Map<string, BrowserSignInPhaseState>();
const listeners = new Set<(state: BrowserSignInPhaseState) => void>();
let seq = 0;

/** The key every report and every card is matched by. '' for a name with no letters or digits. */
export function browserSignInKey(appName: string): string {
  return connectAppOfferKey(appName);
}

export function isBrowserSignInPhase(value: unknown): value is BrowserSignInPhase {
  return typeof value === 'string' && (BROWSER_SIGNIN_PHASES as readonly string[]).includes(value);
}

/** Where the sign-in to `appName` stands in this page, or null when nothing has been reported. */
export function getBrowserSignInPhase(appName: string): BrowserSignInPhaseState | null {
  const key = browserSignInKey(appName);
  return key ? states.get(key) ?? null : null;
}

/** The slug the person's own press registered for `appName`, or ''. */
export function browserSignInSlugFor(appName: string): string {
  return getBrowserSignInPhase(appName)?.slug || '';
}

function publish(state: BrowserSignInPhaseState): void {
  listeners.forEach((listener) => {
    try {
      listener(state);
    } catch {
      // One painter that throws never stops the others.
    }
  });
  try {
    if (typeof document !== 'undefined' && typeof CustomEvent === 'function') {
      document.dispatchEvent(new CustomEvent(BROWSER_SIGNIN_PHASE_EVENT, { detail: { ...state } }));
    }
  } catch {
    // The event is a courtesy for surfaces outside the chat; the listeners above already ran.
  }
}

function setState(
  key: string,
  appName: string,
  phase: BrowserSignInPhase,
  slug: string | undefined,
  now: number | undefined,
  signedInAs?: string,
): BrowserSignInPhaseState {
  const prior = states.get(key);
  seq += 1;
  const who = phase === 'signed-in' ? String(signedInAs || '').trim().slice(0, 120) : '';
  const state: BrowserSignInPhaseState = {
    appKey: key,
    appName: String(appName || '').trim().slice(0, 80) || prior?.appName || key,
    phase,
    ...(slug ? { slug } : {}),
    atMs: typeof now === 'number' ? now : Date.now(),
    seq,
    ...(who ? { signedInAs: who } : {}),
  };
  states.set(key, state);
  publish(state);
  return state;
}

/**
 * Report where the sign-in to `appName` stands. The slug already known stays with it. Returns the new
 * state, or null for an app with no key or a phase this build does not know.
 */
export function reportBrowserSignInPhase(
  appName: string,
  phase: BrowserSignInPhase,
  opts: { now?: number; signedInAs?: string } = {},
): BrowserSignInPhaseState | null {
  const key = browserSignInKey(appName);
  if (!key || !isBrowserSignInPhase(phase)) return null;
  return setState(key, appName, phase, states.get(key)?.slug, opts.now, opts.signedInAs);
}

/**
 * The window for `appName` closed. A sign-in that was on its way or open becomes not-finished; one already
 * signed in (the window closes itself after the person finishes) stays as it is, and so does any other phase.
 */
export function reportBrowserSignInWindowClosed(appName: string, opts: { now?: number } = {}): BrowserSignInPhaseState | null {
  const prior = getBrowserSignInPhase(appName);
  if (!prior || (prior.phase !== 'opening' && prior.phase !== 'open')) return prior;
  return reportBrowserSignInPhase(appName, 'not-finished', opts);
}

/**
 * The slug the person's own press registered arrived (registration answers after the window opens). Kept
 * with the current phase, and told to the cards, so the next press reopens on it. The only writer of a
 * slug; a malformed one changes nothing.
 */
export function rememberBrowserSignInSlug(appName: string, slug: string, opts: { now?: number } = {}): BrowserSignInPhaseState | null {
  const key = browserSignInKey(appName);
  const given = String(slug || '').trim();
  if (!key || !SLUG_RE.test(given)) return getBrowserSignInPhase(appName);
  const prior = states.get(key);
  if (prior?.slug === given) return prior;
  return setState(key, appName, prior?.phase ?? 'not-started', given, opts.now);
}

/** Hear every change, for every app. Returns the way to stop. */
export function subscribeBrowserSignInPhase(listener: (state: BrowserSignInPhaseState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Every sign-in reported in this page (a painter installed late catches up from this). */
export function allBrowserSignInPhases(): BrowserSignInPhaseState[] {
  return Array.from(states.values());
}

/**
 * The phase a connection's RECORD says (the custom-connection roster's status), for a page that has no live
 * report: connected → signed-in, signin-lost → signed-out, registered (a sign-in started and never finished)
 * → not-finished; anything else, or a record that could not be read, says nothing.
 */
export function browserSignInPhaseFromRecord(status: unknown): BrowserSignInPhase | null {
  if (status === 'connected') return 'signed-in';
  if (status === 'signin-lost') return 'signed-out';
  if (status === 'registered') return 'not-finished';
  return null;
}

/** Test seam: forget every report and every listener. */
export function __resetBrowserSignInPhasesForTests(): void {
  states.clear();
  listeners.clear();
  seq = 0;
}
